import React from 'react'
import { Link } from 'react-router-dom'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import {Bell, ShieldCheck, Download, CheckCircle, ArrowRight, MessageSquare, AlertCircle, Clock} from 'lucide-react'

export default function DiscordNotificationPage() {
  const features = [
    {
      icon: Bell,
      title: 'Real-Time Ticket Alerts',
      description: 'Get instant notifications when new support tickets are opened, replied to, or escalated.'
    },
    {
      icon: AlertCircle,
      title: 'Server Status Monitoring',
      description: 'Receive alerts for server uptime issues, resource warnings, and critical system events.'
    },
    {
      icon: MessageSquare,
      title: 'Customizable Webhooks',
      description: 'Configure multiple Discord channels for different departments and priority levels.'
    },
    {
      icon: Clock,
      title: 'Smart Filtering',
      description: 'Set up notification rules to avoid spam and focus on what matters most.'
    }
  ]

  const benefits = [
    'Never miss a critical support ticket',
    'Improve response times by 70%+',
    'Keep your team informed in real-time',
    'Easy setup in under 5 minutes',
    'Compatible with WHMCS 7.x and 8.x',
    'Supports rich embed formatting'
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <ShieldCheck className="w-5 h-5 text-green-500" />
                <span className="text-sm font-semibold text-green-700">Official Licensed Plugin</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                WHMCS Discord Notification Plugin
              </h1>
              
              <p className="text-xl text-slate-600 mb-8">
                Receive instant alerts for support tickets and server status directly in your Discord channel. 
                The essential tool for modern hosting teams.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all hover:shadow-lg">
                  Buy Now - $49
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <a
                  href="#features"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-all"
                >
                  View Features
                </a>
              </div>

              <div className="flex items-center space-x-2 text-sm text-slate-600">
                <Download className="w-4 h-4" />
                <span>Instant digital download • Lifetime updates included</span>
              </div>
            </div>

            <div className="bg-slate-100 rounded-xl p-8 border-2 border-slate-200">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-500 rounded-full"></div>
                  <div>
                    <p className="font-semibold text-slate-900">Support Tickets</p>
                    <p className="text-xs text-slate-500">Discord Channel</p>
                  </div>
                </div>
                <div className="bg-blue-50 rounded p-4 border-l-4 border-blue-500">
                  <p className="text-sm font-medium text-slate-900 mb-1">🎫 New Ticket #1547</p>
                  <p className="text-sm text-slate-600">Client: HostPro Ltd • Priority: High</p>
                  <p className="text-sm text-slate-600 mt-2">"Server not responding to ping requests..."</p>
                  <p className="text-xs text-slate-500 mt-2">Just now</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Powerful Features for Modern Teams
            </h2>
            <p className="text-lg text-slate-600">
              Everything you need to stay connected with your WHMCS operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Hosting Teams Love This Plugin
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{benefit}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-blue-600 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Improve Your Team's Response Time?
            </h3>
            <p className="text-blue-100 mb-6">
              Join hundreds of hosting providers using Discord notifications
            </p>
            <button className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all hover:shadow-xl">
              Purchase Now - $49
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border-2 border-blue-600 rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <p className="text-sm font-semibold text-blue-600 mb-2">ONE-TIME PAYMENT</p>
              <p className="text-5xl font-bold text-slate-900 mb-2">$49</p>
              <p className="text-slate-600">Lifetime license • Free updates</p>
            </div>

            <div className="space-y-3 mb-8">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">Unlimited Discord channels</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">All WHMCS versions supported</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">Priority email support</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">30-day money-back guarantee</span>
              </div>
            </div>

            <button className="w-full px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all hover:shadow-lg">
              Add to Cart
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
